import {
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  sendEmailVerification,
  updateProfile,
} from "firebase/auth";
import { auth } from "../firebase";
import {
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  Divider,
  Avatar,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import React, { useState, useEffect } from "react";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isSignup, setIsSignup] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/map";

  const getFriendlyErrorMessage = (code) => {
    switch (code) {
      case "auth/email-already-in-use":
        return "This email is already registered. Please log in or use a different email.";
      case "auth/invalid-email":
        return "The email address is not valid. Please check and try again.";
      case "auth/user-not-found":
        return "No account found with this email. Please sign up.";
      case "auth/wrong-password":
        return "Incorrect password. Please try again.";
      case "auth/weak-password":
        return "Password should be at least 6 characters long.";
      case "auth/network-request-failed":
        return "Network error. Please check your internet connection.";
      case "auth/too-many-requests":
        return "Too many attempts. Please try again later.";
      default:
        return "An unexpected error occurred. Please try again.";
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUser(user); // Set user state
        if (user.emailVerified) {
          onLogin(user);
          navigate(redirectTo); // Redirect to the intended page or home
        }
      }
    });
    return () => unsubscribe();
  }, [navigate, onLogin, redirectTo]);

  const getAvatarContent = () => {
    if (user?.photoURL) {
      return (
        <Avatar
          alt={user.displayName || "User"}
          src={user.photoURL}
          sx={{ width: 64, height: 64, margin: "auto", marginBottom: "16px" }}
        />
      );
    } else if (user?.displayName) {
      const initials = user.displayName
        .split(" ")
        .map((name) => name[0])
        .join("")
        .toUpperCase()
        .slice(0, 2); // Get first 2 letters max
      return (
        <Avatar
          sx={{
            bgcolor: "#8C1515",
            color: "white",
            width: 64,
            height: 64,
            fontSize: "24px",
            fontWeight: "600",
            margin: "auto",
            marginBottom: "16px",
          }}
        >
          {initials}
        </Avatar>
      );
    } else if (user?.email) {
      // Fallback to first letter of email if no display name
      const initial = user.email[0].toUpperCase();
      return (
        <Avatar
          sx={{
            bgcolor: "#8C1515",
            color: "white",
            width: 64,
            height: 64,
            fontSize: "24px",
            fontWeight: "600",
            margin: "auto",
            marginBottom: "16px",
          }}
        >
          {initial}
        </Avatar>
      );
    } else {
      return (
        <Avatar
          sx={{
            bgcolor: "#8C1515",
            color: "white",
            width: 64,
            height: 64,
            fontSize: "24px",
            fontWeight: "600",
            margin: "auto",
            marginBottom: "16px",
          }}
        >
          U
        </Avatar>
      );
    }
  };

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await setPersistence(auth, browserLocalPersistence);
      const result = await signInWithPopup(auth, provider);
      setUser(result.user); // Update user state
      onLogin(result.user);
      navigate(redirectTo); // Redirect to the intended page
    } catch (err) {
      setError(getFriendlyErrorMessage(err.code));
    }
  };

  const handleEmailPasswordAuth = async (event) => {
    event.preventDefault();
    
    // Validation for signup
    if (isSignup && !name.trim()) {
      setError("Please enter your name");
      return;
    }
    
    try {
      await setPersistence(auth, browserLocalPersistence);
      if (isSignup) {
        const result = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
        
        // Update the user's display name
        if (name.trim()) {
          await updateProfile(result.user, {
            displayName: name.trim()
          });
        }
        
        await sendEmailVerification(result.user);
        setInfo("Verification email sent! Please check your inbox (and spam folder) to verify your email address.");
        setUser({...result.user, displayName: name.trim()}); // Update user state with display name
      } else {
        const result = await signInWithEmailAndPassword(auth, email, password);
        if (!result.user.emailVerified) {
          setError("Please verify your email before logging in.");
          return;
        }
        setUser(result.user); // Update user state
        onLogin(result.user);
        navigate(redirectTo); // Redirect to the intended page
      }
    } catch (err) {
      setError(getFriendlyErrorMessage(err.code));
    }
  };

  return (
    <Paper
      elevation={3}
      style={{
        padding: "24px",
        maxWidth: "400px",
        margin: "auto",
        marginTop: "100px",
      }}
    >
      {user && getAvatarContent()}
      <Typography variant="h5" align="center" gutterBottom>
        {isSignup ? "Sign Up" : "Log In"}
      </Typography>
      {error && (
        <Typography variant="body2" color="error" align="center">
          {error}
        </Typography>
      )}
      {info && (
        <Typography variant="body2" color="primary" align="center">
          {info}
        </Typography>
      )}
      <Box
        component="form"
        onSubmit={handleEmailPasswordAuth}
        style={{ marginBottom: "16px" }}
      >
        {isSignup && (
          <TextField
            label="Name"
            type="text"
            fullWidth
            margin="normal"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter your name"
            required
            autoFocus
          />
        )}
        <TextField
          label="Email"
          type="email"
          fullWidth
          margin="normal"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus={!isSignup}
        />
        <TextField
          label="Password"
          type="password"
          fullWidth
          margin="normal"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          style={{ marginTop: "16px" }}
        >
          {isSignup ? "Sign Up" : "Log In"}
        </Button>
      </Box>
      <Divider style={{ margin: "16px 0" }}>OR</Divider>
      <Button
        variant="outlined"
        color="secondary"
        fullWidth
        onClick={handleGoogleLogin}
      >
        Continue with Google
      </Button>
      <Typography align="center" style={{ marginTop: "16px" }}>
        {isSignup ? (
          <>
            Already have an account?{" "}
            <Button
              color="primary"
              onClick={() => {
                setIsSignup(false);
                setError(""); // Clear error when switching modes
                setInfo(""); // Clear info when switching modes
                setName(""); // Clear name when switching to login
              }}
            >
              Log In
            </Button>
          </>
        ) : (
          <>
            Don't have an account?{" "}
            <Button
              color="primary"
              onClick={() => {
                setIsSignup(true);
                setError(""); // Clear error when switching modes
                setInfo(""); // Clear info when switching modes
                setName(""); // Clear name when switching to signup
              }}
            >
              Sign Up
            </Button>
          </>
        )}
      </Typography>
    </Paper>
  );
};

export default Login;
